
import React, { useState, useMemo, useEffect } from "react";
import CheckBox from "../common/Check-box";


export default function DiscountedServices({
  services = [],
  categories = [],
  defaultServices = [],
  onSelect,
}) {
  const [selectedServices, setSelectedServices] = useState([]);

  // --- initialize once ---
  useEffect(() => {
    if (!services.length) return;

    setSelectedServices(() => {
      if (Array.isArray(defaultServices)) {
        if (defaultServices.length === 0) {
          // empty array means "select all"
          return services.map((s) => s.id);
        }
        // select only valid service IDs
        return services
          .filter((s) => defaultServices.includes(s.id))
          .map((s) => s.id);
      }
      return [];
    });
  }, [services]);

  // --- group services by category ---
  const categorized = useMemo(
    () =>
      categories.map((cat) => ({
        ...cat,
        services: services.filter((s) => cat.services.includes(s.id)),
      })),
    [categories, services]
  );

  const uncategorized = useMemo(() => {
    const categorizedIds = categories.flatMap((cat) => cat.services);
    return services.filter((s) => !categorizedIds.includes(s.id));
  }, [services, categories]);

  // --- toggle handlers ---
  const toggleService = (id) => {
    setSelectedServices((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };

  const toggleCategory = (cat) => {
    const ids = cat.services.map((s) => s.id);
    const allChecked = ids.every((id) => selectedServices.includes(id));
    setSelectedServices((prev) =>
      allChecked
        ? prev.filter((id) => !ids.includes(id))
        : [...new Set([...prev, ...ids])]
    );
  };

  const toggleAll = () => {
    const allIds = services.map((s) => s.id);
    const allChecked = allIds.every((id) => selectedServices.includes(id));
    setSelectedServices(allChecked ? [] : allIds);
  };

  const allSelected =
    services.length > 0 &&
    services.every((s) => selectedServices.includes(s.id));

  // --- notify parent ---
  useEffect(() => {
    if (onSelect) onSelect(selectedServices);
  }, [selectedServices, onSelect]);

  // --- render ---
  return (
    <div className="discounted-services-container">
      <div className="discounted-services-header">
        <p className="discounted-services-title">Applies To:</p>
      </div>

      <div className="discounted-section">
        {/* All Services */}
        <div className="category-block">
          <CheckBox
            label="All Services"
            name="services_all"
            value="all"
            checked={allSelected}
            onChange={toggleAll}
          />
        </div>

        {/* Uncategorized services */}
        {uncategorized.length > 0 &&
          uncategorized.map((service) => (
            <CheckBox
              key={service.id}
              label={service.name}
              value={service.id}
              name="services[]"
              checked={selectedServices.includes(service.id)}
              onChange={() => toggleService(service.id)}
            />
          ))}

        {/* Categorized services */}
        {categorized.length > 0 &&
          categorized.map((cat) => (
            <div key={cat.id}>
              <CheckBox
                label={cat.name}
                name={`category_${cat.id}`}
                value={cat.id}
                checked={
                  cat.services.length > 0 &&
                  cat.services.every((s) => selectedServices.includes(s.id))
                }
                onChange={() => toggleCategory(cat)}
              />

              <div className="services-list">
                {cat.services.map((service) => (
                  <CheckBox
                    key={service.id}
                    label={service.name}
                    value={service.id}
                    name="services[]"
                    checked={selectedServices.includes(service.id)}
                    onChange={() => toggleService(service.id)}
                  />
                ))}
              </div>
            </div>
          ))}

        {/* Empty state */}
        {services.length === 0 && (
          <p className="empty-text">No services found.</p>
        )}
      </div>
    </div>
  );
}

